import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Layers,
  ChevronRight,
  Receipt,
} from 'lucide-react';
import { PageHeader, PageLoader } from '@repo/ui';

// ── helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, lang: string) {
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'en-US', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount ?? 0);
}

function formatDate(dateStr: string, lang: string) {
  return new Date(dateStr).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getStatusMeta(status: string) {
  switch (status?.toLowerCase()) {
    case 'paid':
      return {
        label: 'paid',
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/20',
        barColor: '#10b981',
        icon: CheckCircle2,
      };
    case 'partially_paid':
      return {
        label: 'partial',
        color: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/20',
        barColor: '#f59e0b',
        icon: Clock,
      };
    case 'unpaid':
      return {
        label: 'unpaid',
        color: 'text-rose-600 dark:text-rose-400',
        bg: 'bg-rose-500/10',
        border: 'border-rose-500/20',
        barColor: '#ef4444',
        icon: XCircle,
      };
    case 'refunded':
      return {
        label: 'refunded',
        color: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/20',
        barColor: '#3b82f6',
        icon: RotateCcw,
      };
    default:
      return {
        label: status ?? 'unknown',
        color: 'text-muted-foreground',
        bg: 'bg-muted',
        border: 'border-border',
        barColor: '#94a3b8',
        icon: Clock,
      };
  }
}

// ── Payment progress bar ──────────────────────────────────────────────────────

function ProgressBar({ paid, total, color }: { paid: number; total: number; color: string }) {
  const pct = total > 0 ? Math.min((paid / total) * 100, 100) : 0;
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ── Invoice card ──────────────────────────────────────────────────────────────

function InvoiceCard({ order, lang, onPress }: { order: any; lang: string; onPress: () => void }) {
  const { t } = useTranslation();
  const meta = getStatusMeta(order.status);
  const StatusIcon = meta.icon;
  const trackName =
    order.items?.[0]?.programName ??
    order.items?.[0]?.program?.track?.name ??
    t('student.billing.dashboard.fallbackProgram');
  const programCount = order.items?.length ?? 0;

  return (
    <div
      onClick={onPress}
      className="group rounded-2xl border border-border bg-card shadow-sm overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 hover:border-primary/20"
    >
      {/* Accent line */}
      <div className="h-0.5 w-full" style={{ backgroundColor: meta.barColor }} />

      <div className="p-5 space-y-4">
        {/* Header row */}
        <div className="flex items-center gap-3">
          {/* Status icon */}
          <div
            className={`h-11 w-11 rounded-2xl ${meta.bg} flex items-center justify-center shrink-0`}
          >
            <StatusIcon className={`h-5 w-5 ${meta.color}`} />
          </div>

          {/* Order number + date */}
          <div className="flex-1 min-w-0">
            <p className="font-black text-sm text-foreground font-mono">{order.number}</p>
            <p className="text-xs text-muted-foreground">{formatDate(order.createdAt, lang)}</p>
          </div>

          {/* Status badge */}
          <span
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-black ${meta.bg} ${meta.color} border ${meta.border}`}
          >
            {t(`common.${meta.label}`, { defaultValue: meta.label })}
          </span>
        </div>

        {/* Track name */}
        <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2">
          <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-sm font-medium text-muted-foreground truncate">
            {trackName}
            {programCount > 1
              ? ` (${t('student.billing.dashboard.moreCount', { count: programCount - 1 })})`
              : ''}
          </p>
        </div>

        {/* Financials */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{t('student.payments.amount')}</span>
            <span className="text-base font-black">{formatCurrency(order.netTotal, lang)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {t('student.payments.paidAmount')}
            </span>
            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
              {formatCurrency(order.paidTotal, lang)}
            </span>
          </div>
          {order.remaining > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t('student.payments.remaining')}
              </span>
              <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
                {formatCurrency(order.remaining, lang)}
              </span>
            </div>
          )}

          {/* Progress bar */}
          <ProgressBar paid={order.paidTotal} total={order.netTotal} color={meta.barColor} />
        </div>

        {/* Footer tap hint */}
        <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground/50 group-hover:text-primary transition-colors">
          <span>{t('student.billing.dashboard.tapForDetails')}</span>
          <ChevronRight className={`h-4 w-4 ${lang === 'ar' ? 'rotate-180' : ''}`} />
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const PaymentsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['student-payments-summary'],
    queryFn: () =>
      api
        .get('/student/invoices/summary')
        .catch(() => api.get('/student/payments/summary'))
        .then((res) => res.data?.data),
  });

  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['student-payments'],
    queryFn: () =>
      api
        .get('/student/invoices')
        .catch(() => api.get('/student/payments'))
        .then((res) => res.data?.data),
  });

  const isLoading = summaryLoading || paymentsLoading;

  if (isLoading) {
    return <PageLoader message={t('common.loading')} />;
  }

  const summaryCards = summary
    ? [
        {
          label: t('student.payments.total'),
          value: formatCurrency(summary.totalAmount ?? 0, lang),
          icon: CreditCard,
          color: 'text-indigo-600',
          bg: 'bg-indigo-500/10',
        },
        {
          label: t('student.payments.paid'),
          value: formatCurrency(summary.totalPaid ?? 0, lang),
          icon: TrendingUp,
          color: 'text-emerald-600',
          bg: 'bg-emerald-500/10',
        },
        {
          label: t('student.payments.remaining'),
          value: formatCurrency(summary.totalRemaining ?? 0, lang),
          icon: Clock,
          color: 'text-amber-600',
          bg: 'bg-amber-500/10',
        },
        {
          label: t('student.payments.orders'),
          value: summary.totalOrders ?? 0,
          icon: Layers,
          color: 'text-violet-600',
          bg: 'bg-violet-500/10',
        },
      ]
    : [];

  return (
    <div className="space-y-6 pb-12">
      <PageHeader
        title={t('student.payments.title')}
        subtitle={t('student.payments.subtitle', {
          defaultValue: 'Manage your invoices, track payments, and view your billing history',
        })}
        icon={CreditCard}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {/* Summary Cards */}
      {summaryCards.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryCards.map((card, i) => (
            <div
              key={i}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 duration-300"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1 min-w-0">
                  <p className="text-xs font-bold text-muted-foreground leading-tight">
                    {card.label}
                  </p>
                  <p className="text-lg font-black truncate">{card.value}</p>
                </div>
                <div
                  className={`shrink-0 p-2.5 rounded-xl ${card.bg} transition-all group-hover:rotate-12 duration-300`}
                >
                  <card.icon className={`h-5 w-5 ${card.color}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Enrollment list header */}
      {payments?.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-bold text-muted-foreground">
            {t('student.payments.enrollmentsCount', {
              count: payments.length,
              defaultValue:
                lang === 'ar'
                  ? `${payments.length} تسجيل`
                  : `${payments.length} Enrollment${payments.length !== 1 ? 's' : ''}`,
            })}
          </p>
        </div>
      )}

      {/* Invoice cards */}
      {!payments?.length ? (
        <div className="flex flex-col items-center justify-center py-24 rounded-3xl border-2 border-dashed border-muted/50 bg-muted/10 space-y-3">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
            <Receipt className="h-8 w-8 text-muted-foreground/30" />
          </div>
          <p className="font-bold text-muted-foreground">
            {t('student.payments.noPayments', { defaultValue: 'No invoices yet.' })}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {payments.map((order: any) => (
            <InvoiceCard
              key={order._id}
              order={order}
              lang={lang}
              onPress={() => navigate(`/payments/${order.number}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
